import Image from 'next/image';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import beTwMerge from '@/utils/beTwMerge';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import { Action } from '@/components/SingleImageTile/SingleImageTile';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import RichText from '../../RichText/RichText';
import Title from '../../Title/Title';

interface ITitle {
  content: string;
  titleHTMLTag?: 'h1' | 'h2' | 'h3';
  titleSize?: 'extraLarge' | 'large' | 'medium' | 'small';
  titleWeight?: 'light' | 'normal' | 'medium' | 'bold' | 'black';
}

interface ICopyBlockProps {
  /**
   * Optional large header.
   * - content*: The content of the header
   * - titleHTMLTag: Heading level that will be used to render the optional header. Defaults to 'h2'
   * - titleSize: Select one of the styles 'extraLarge' | 'large' | 'medium' | 'small'.
   * - titleWeight: Optional header weight. Defaults to 'normal'
   */
  optionalLargeHeader?: ITitle;
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * Optional main image of the copy block
   */
  mainImage?: string;
  /**
   *  Uploaded video. If present it goes instead of the main image
   */
  mainVideo?: string;
  /**
   *  URI link for a video. If present it goes instead of the main image
   */
  videoURI?: string;
  /**
   * Alt text of the image for accessibility
   */
  altImage?: string;
  /**
   * To position the image either to the left or to the right
   */
  imagePosition: string;
  /**
   *  Rich text content of the Block
   */
  content: string;
  /**
   * The list of cta buttons
   * Props.
   * - text*: Text of the button
   * - url*: url to link to
   * - newTab*: If the link should open in a new tab
   */
  actions?: Action[];
  /**
   * Has Ring sizer Form
   */
  hasRingSizerForm: boolean;
  /*
   * Video title description for screen readers
   */
  title?: string;
}

export default function CopyBlock({
  mainImage,
  mainVideo,
  videoURI,
  altImage,
  imagePosition,
  content,
  actions,
  optionalLargeHeader,
  anchorLinkID,
  hasRingSizerForm,
  margin = false,
  title = 'Wistita video',
}: ICopyBlockProps) {
  const formattedActions = actions?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));
  const isThereMedia = mainImage || mainVideo || videoURI;
  const isMediaHidden = imagePosition === 'none';

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={classNames(
        'bgc-copy-block tw-w-[85%] tablet:tw-w-[85%] tw-max-w-[1440px] tw-mx-auto',
        getComponentMargin(margin)
      )}
    >
      {optionalLargeHeader?.content && (
        <Title
          component={optionalLargeHeader?.titleHTMLTag || 'h2'}
          weight={optionalLargeHeader?.titleWeight}
          size={optionalLargeHeader?.titleSize}
          alignment="center"
        >
          {optionalLargeHeader?.content}
        </Title>
      )}
      <div
        className={classNames(
          'tw-flex tw-items-center lg:tw-mx-auto lg:tw-items-start tw-flex-col lg:tw-flex-row tw-max-w-[1172px] tw-w-full lg:tw-w-full tw-gap-x-[64px]',
          {
            'lg:tw-max-w-[1440px]': isThereMedia,
            'lg:tw-max-w-[830px]': isMediaHidden || (!mainImage && !mainVideo && !videoURI),
            'lg:tw-flex-row-reverse': imagePosition === 'right',
          }
        )}
      >
        {isThereMedia && !isMediaHidden && (
          <div className="tw-w-full">
            {mainImage && !mainVideo && !videoURI && (
              <div className="tw-w-full tw-relative" style={{ paddingTop: '68.1%' }}>
                <Image
                  alt={altImage || ''}
                  src={mainImage}
                  loading="lazy"
                  fill
                  sizes="(min-width: 768px) 50vw, 100vw"
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </div>
            )}

            {mainVideo && !videoURI && (
              <video className="tw-aspect-[367/250] tw-object-cover tw-w-full" src={mainVideo} autoPlay muted loop />
            )}

            {videoURI && (
              <iframe
                title={title}
                src={videoURI}
                className="tw-aspect-[367/250] tw-object-cover tw-w-full tw-border-none tw-outline-none"
              />
            )}
          </div>
        )}

        <div
          className={classNames('tw-w-full tw-max-w-[1172px] lg:tw-mt-0', {
            'tw-mt-be-6': isThereMedia && !isMediaHidden,
          })}
        >
          <RichText content={content} />

          {formattedActions && formattedActions?.length > 0 && (
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-flex-row tw-gap-2 md:tw-gap-4 tw-w-full tw-mt-8">
              {formattedActions?.slice(0, 4).map((action, i) => {
                const { label, color, type } = action;

                if (action.actions) {
                  const { actions: subActions } = action;
                  return (
                    <DropdownButton
                      key={i}
                      label={label}
                      options={subActions}
                      // @ts-ignore
                      color={color}
                      type={type}
                      className={beTwMerge(
                        `tw-basis-[calc(50%-8px)] tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis`
                      )}
                    />
                  );
                }
                const { onClick, href } = action;
                return (
                  <Button
                    className={beTwMerge(
                      `tw-basis-[calc(50%-8px)] tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden`
                    )}
                    ariaLabel={moreDescriptiveAriaLabel(title, label)}
                    key={i}
                    label={label}
                    // @ts-ignore
                    color={color}
                    type={type}
                    href={href}
                    onClick={onClick}
                  />
                );
              })}
            </div>
          )}
          {hasRingSizerForm &&
            (() => {
              const RingSizeForm = dynamic(() => import('@/components/RingSizeForm/RingSizeForm'));
              return <RingSizeForm />;
            })()}
        </div>
      </div>
    </div>
  );
}
